// Pure helpers for the Branch Cash evidence picker - unit-tested without rendering. These only stop an obviously unsuitable pick before
// it is uploaded; the server decides the real type from the file's content and enforces the size, the branch and the company.

export const EVIDENCE_MAX_BYTES = 5 * 1024 * 1024;
export const EVIDENCE_MAX_FILES = 10;
export const EVIDENCE_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'webp'] as const;
export const EVIDENCE_ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp';
export const EVIDENCE_LIMITS_TEXT = 'PDF, JPEG, PNG or WebP - up to 5 MB each';
export type EvidenceOwnerType = 'EXPENSE' | 'RECEIPT' | 'ADJUSTMENT';

export interface EvidenceItem { id: string; originalName: string; sizeBytes: number; mimeType: string }

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** '' when the pick looks acceptable, else a plain-language reason. `already` = files this form already holds. */
export function evidenceFileError(file: { name: string; size: number }, already: number): string {
  if (already >= EVIDENCE_MAX_FILES) return `At most ${EVIDENCE_MAX_FILES} files can be attached`;
  if (file.size === 0) return `${file.name} is empty`;
  if (file.size > EVIDENCE_MAX_BYTES) return `${file.name} is ${formatBytes(file.size)} - the limit is ${formatBytes(EVIDENCE_MAX_BYTES)}`;
  const m = /\.([A-Za-z0-9]{1,8})$/.exec(file.name);
  const ext = m ? m[1].toLowerCase() : '';
  if (!(EVIDENCE_EXTENSIONS as readonly string[]).includes(ext)) return `${file.name}: only PDF, JPEG, PNG or WebP files can be attached`;
  return '';
}

/** Short label for a file chip / list row. */
export const evidenceLabel = (f: EvidenceItem): string => `${f.originalName} (${formatBytes(f.sizeBytes)})`;

/** Does the form hold evidence of any kind? A text reference OR at least one uploaded file. */
export const hasEvidence = (ref: string, fileCount: number): boolean => ref.trim().length > 0 || fileCount > 0;
